/* eslint-disable brace-style */

import { Component, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
} from '@next/core-lib/dialog';
import { AgrifirmCrop, CropData, CropFieldAdvice } from 'src/app/shared/models';

export interface SelectCropFieldsDialogItem {
  value: CropFieldAdvice;
  name: string;
  description?: string;
}

export interface SelectCropFieldsDialogData extends AbstractFormDialogData {
  options: SelectCropFieldsDialogItem[];
  selected: CropFieldAdvice[];
  emptyText: string;
  disableSelectAll?: boolean;
  crops: CropData[];
  selectedCropIds: number[];
}

export interface SelectCropFieldsResult {
  options: CropFieldAdvice[];
}

@Component({
  templateUrl: './select-cropfields-dialog.component.html',
  styleUrls: ['./select-cropfields-dialog.component.scss'],
  standalone: false,
})
export class SelectCropFieldsDialogComponent
  extends AbstractFormDialog<CropFieldAdvice[]>
  implements OnInit
{
  data = inject<SelectCropFieldsDialogData>(MAT_DIALOG_DATA);
  private readonly formBuilder = inject(FormBuilder);
  protected readonly mdDialogRef = inject<MatDialogRef<SelectCropFieldsDialogComponent, AbstractDialogResultData<CropFieldAdvice[]>>>(MatDialogRef);

  formGroup = this.formBuilder.group({
    options: this.formBuilder.array(
      [
        ...this.data.options.map(
          (option) =>
            new FormControl(
              this.data.selected
                ? this.data.selected.indexOf(option.value) === -1
                  ? false
                  : true
                : false,
            ),
        ),
      ],
      Validators.required,
    ),
  });
  areAllOptionsSelected = false;
  previousSelectedCropIds: number[] = [];
  relevantCrops: AgrifirmCrop[] = [];

  get formOptions() {
    return (this.formGroup.controls.options as FormArray).controls as FormControl[];
  }

  ngOnInit() {
    this.select();

    this.relevantCrops = this.data.crops
      .filter((crop) => this.data.options.some((option) => option.value.crop?.id === crop.id))
      .map((crop) => ({ id: crop.id, name: crop.agrifirmCrop.name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    this.previousSelectedCropIds = this.data.selectedCropIds;
  }

  selectAllChanged(selectAllValue: boolean) {
    const options = this.formGroup.get('options');
    const values = options.value as boolean[];
    options.setValue(values.map(() => selectAllValue));
    this.areAllOptionsSelected = selectAllValue;
  }

  select() {
    const options = this.formGroup.get('options');
    const values = options.value as boolean[];
    this.areAllOptionsSelected = values.every((option) => option === true);
  }

  confirmSelectedValues(result: SelectCropFieldsResult) {
    super.confirm(
      result.options
        .map((isEnabled, index) =>
          !!isEnabled && this.isCropSelected(this.data.options[index].value.crop?.id)
            ? this.data.options[index].value
            : null,
        )
        .filter(Boolean),
    );
  }

  someSelected() {
    const options = this.formGroup.get('options');
    const values = options.value as boolean[];
    return values.find((option) => option === true) && !this.areAllOptionsSelected;
  }

  isCropSelected = (cropId: number): boolean =>
    this.data.selectedCropIds.includes(-1) || this.data.selectedCropIds.includes(cropId);

  cropSelectionChange(_: void): void {
    if (this.data.selectedCropIds.includes(-1) && !this.previousSelectedCropIds.includes(-1)) {
      this.data.selectedCropIds = [-1];
    } else if (
      this.previousSelectedCropIds.includes(-1) &&
      !this.data.selectedCropIds.includes(-1)
    ) {
      this.data.selectedCropIds = this.data.selectedCropIds.filter((cropId) => cropId !== -1);
    }

    this.previousSelectedCropIds = this.data.selectedCropIds;
  }

  getVisibleCropFieldCount = (): number =>
    this.data.options.filter((option) => this.isCropSelected(option.value.crop?.id)).length;
}
